import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { SET_SIGNUP_SLIDE_AS_SEEN } from '../../http/apollo-queries';
import { Button } from '../../components/UI';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/Auth';
import { Modal } from '../../components/Modal';

const Onboarding = () => {
  const { t } = useTranslation();
  const { refetch: refetchUser } = useAuth();
  const slides = [
    {
      title: t('onBoarding.slide1.title'),
      image: '/onboarding_slide__1.svg',
      description: t('onBoarding.slide1.desc'),
    },
    {
      title: t('onBoarding.slide2.title'),
      image: '/onboarding_slide__2.svg',
      description: t('onBoarding.slide2.desc'),
    },
    {
      title: t('onBoarding.slide3.title'),
      image: '/onboarding_slide__3.svg',
      description: t('onBoarding.slide3.desc'),
    },
    {
      title: t('onBoarding.slide4.title'),
      image: '/onboarding_slide__4.svg',
      description: t('onBoarding.slide4.desc'),
    },
    {
      title: t('onBoarding.slide5.title'),
      image: '/onboarding_slide__5.svg',
      description: t('onBoarding.slide5.desc'),
    },
    {
      title: t('onBoarding.slide6.title'),
      image: '/onboarding_slide__6.svg',
      description: t('onBoarding.slide6.desc'),
    },
  ];

  const [step, setStep] = useState<number>(0);
  const navigate = useNavigate();
  const [isSkipLoading, setIsSkipLoading] = useState<boolean>(false);
  const [isNextLoading, setIsNextLoading] = useState<boolean>(false);

  const [setSignupSlideAsSeen] = useMutation(SET_SIGNUP_SLIDE_AS_SEEN);

  const next = () => {
    setIsNextLoading(true);

    const nextStep = step + 1;

    if (slides && nextStep >= slides.length) {
      setSignupSlideAsSeen()
        .then(refetchUser)
        .then(() => navigate('/choose-theme'));
    } else {
      setStep(nextStep);
      setIsNextLoading(false);
    }
  };

  const skip = () => {
    setIsSkipLoading(true);
    setSignupSlideAsSeen()
      .then(refetchUser)
      .then(() => {
        setIsSkipLoading(false);
        navigate('/choose-theme');
      });
  };

  return (
    <Modal
      isOpen={true}
      backdrop={'solid'}
      handleMerlinLogo={true}
      isClosable={false}
      padding={'base'}
    >
      <>
        <div className={'flex flex-col flex-grow-1 justify-center'}>
          <img
            src={slides[step].image}
            alt={'slide-' + step}
            className={'h-32'}
          />
          <h1 className="text-black text-xl font-semibold text-center mt-8">
            {slides[step].title}
          </h1>
          <p className="mt-4 text-grey-50 text-sm text-center px-8 lg:px-32">
            {slides[step].description}
          </p>
        </div>
        <div className={'flex justify-between w-full items-center'}>
          <Button
            type="button"
            variant="transparent"
            loading={isSkipLoading}
            buttonClass="text-sm font-semibold"
            onClick={() => skip()}
          >
            {t('onBoarding.skip')}
          </Button>

          <div className={'flex flex-row'}>
            {slides.map((slide, index) => (
              <div
                className={`rounded-full w-1 h-1 m-1 cursor-pointer
                ${step === index ? 'bg-lime' : 'bg-grey-10'}
                `}
                key={index}
                onClick={() => {
                  setStep(index);
                }}
              ></div>
            ))}
          </div>
          <Button
            type="button"
            variant="primary"
            loading={isNextLoading}
            buttonClass="text-sm font-semibold"
            onClick={() => next()}
          >
            {t('onBoarding.continue')}
          </Button>
        </div>
      </>
    </Modal>
  );
};

export default Onboarding;
